import type { Editor } from '@tiptap/core';

export interface ToolbarState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  h1: boolean;
  h2: boolean;
  h3: boolean;
  bullet: boolean;
  ordered: boolean;
  quote: boolean;
  right: boolean;
  center: boolean;
  left: boolean;
  justify: boolean;
  link: boolean;
  linkHref: string;
  color: string | null;
  inTable: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Which toolbar buttons show as "on", read from the editor's CURRENT selection.
 *
 * Only while the writing area has focus: an editor that is not being typed in
 * still remembers a selection — after a version is copied in it sits at the
 * very end of the text, inside a list if the text ends with one — and lighting
 * buttons up for a cursor nobody can see read as "this paragraph is a list".
 * Undo and redo do not depend on the cursor, so they always show.
 */
export function toolbarState(e: Editor, focused: boolean): ToolbarState {
  const on = (fn: () => boolean) => focused && fn();
  return {
    bold: on(() => e.isActive('bold')),
    italic: on(() => e.isActive('italic')),
    underline: on(() => e.isActive('underline')),
    strike: on(() => e.isActive('strike')),
    h1: on(() => e.isActive('heading', { level: 1 })),
    h2: on(() => e.isActive('heading', { level: 2 })),
    h3: on(() => e.isActive('heading', { level: 3 })),
    bullet: on(() => e.isActive('bulletList')),
    ordered: on(() => e.isActive('orderedList')),
    quote: on(() => e.isActive('blockquote')),
    right: on(() => e.isActive({ textAlign: 'right' })),
    center: on(() => e.isActive({ textAlign: 'center' })),
    left: on(() => e.isActive({ textAlign: 'left' })),
    justify: on(() => e.isActive({ textAlign: 'justify' })),
    link: on(() => e.isActive('link')),
    linkHref: focused ? ((e.getAttributes('link').href as string | undefined) ?? '') : '',
    color: focused ? ((e.getAttributes('textStyle').color as string | undefined) ?? null) : null,
    inTable: on(() => e.isActive('table')),
    canUndo: e.can().undo(),
    canRedo: e.can().redo(),
  };
}
